import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import "../readable-form.scss";

const opt = (arr: string[]) => arr.map((v) => ({ value: v, label: v }));
const READINESS = opt(["Ready Now", "Ready in 1 year", "Ready in 2 years", "Not Ready"]);
const LEVEL = opt(["High", "Medium", "Low"]);

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const AssessCandidate = (props: any) => {
  const { candidate, setShowAssess, setAssessData, onSaved } = props;
  const dispatch = useDispatch();
  const [history, setHistory] = useState<any[]>([]);

  // Optional-chained: the modal stays mounted through its fade-out, and saving nulls
  // the candidate before that finishes.
  const [form, setForm] = useState<any>({
    readiness: candidate?.readiness ?? "",
    performance: candidate?.performance ?? "",
    potential: candidate?.potential ?? "",
    riskOfLoss: candidate?.riskOfLoss ?? "",
    notes: candidate?.notes ?? "",
  });
  const [btnDisable, setBtnDisable] = useState(false);

  const userLogin: any = localStorage.getItem("login");
  const loginId = JSON.parse(userLogin || "{}")?.id;

  // Past assessments, so a reassessment is made against what was recorded last time
  // rather than from memory — the point of the cycle is the movement between them.
  useEffect(() => {
    if (!candidate?._id) return;
    dispatch(
      actions.getAssessmentHistoryAction(candidate._id, (res: any) => {
        if (res?.data) setHistory(res.data);
      })
    );
  }, [candidate?._id]);

  const sel = (v: string) => (v ? { value: v, label: v } : null);

  const handleSave = () => {
    setBtnDisable(true);
    dispatch(
      actions.saveAssessmentAction(
        candidate._id,
        { ...form, notes: form.notes?.trim(), updatedBy: loginId },
        (res: any) => {
          setBtnDisable(false);
          if (res?.error) {
            const msg =
              res?.error?.response?.data?.error?.error_description ||
              "Failed to save assessment";
            showErrorToastMessage(msg);
          } else if (res?.data) {
            showSuccessToastMessage("Assessment saved");
            setShowAssess(false);
            setAssessData(null);
            onSaved();
          }
        }
      )
    );
  };

  return (
    <div className="readable-form">
      <div
        className="mb-3 p-2"
        style={{ background: "#f1f3f5", borderRadius: "6px" }}
      >
        KRA Score <span style={{ color: "#767b81" }}>(from Resource Allocation)</span>:{" "}
        {candidate?.weightedScoreSnapshot === null ||
        candidate?.weightedScoreSnapshot === undefined ? (
          <b style={{ color: "#767b81" }}>Not scored yet</b>
        ) : (
          <b>{candidate.weightedScoreSnapshot}</b>
        )}
      </div>

      <div className="row">
        <div className="col-md-6 mt-2">
          <label className="form-label">Readiness</label>
          <div className="hint">How soon could they step into the role?</div>
          <Select className="dropdown-select" options={READINESS} value={sel(form.readiness)}
            onChange={(s: any) => setForm({ ...form, readiness: s?.value || "" })} placeholder="Select" />
        </div>
        <div className="col-md-6 mt-2">
          <label className="form-label">Performance</label>
          <div className="hint">How well are they doing today?</div>
          <Select className="dropdown-select" options={LEVEL} value={sel(form.performance)}
            onChange={(s: any) => setForm({ ...form, performance: s?.value || "" })} placeholder="Select" />
        </div>
        <div className="col-md-6 mt-3">
          <label className="form-label">Potential</label>
          <div className="hint">Can they grow into bigger roles?</div>
          <Select className="dropdown-select" options={LEVEL} value={sel(form.potential)}
            onChange={(s: any) => setForm({ ...form, potential: s?.value || "" })} placeholder="Select" />
        </div>
        <div className="col-md-6 mt-3">
          <label className="form-label">Risk of Loss</label>
          <div className="hint">How likely are we to lose them?</div>
          <Select className="dropdown-select" options={LEVEL} value={sel(form.riskOfLoss)}
            onChange={(s: any) => setForm({ ...form, riskOfLoss: s?.value || "" })} placeholder="Select" />
        </div>
      </div>

      <div className="mt-3">
        <label className="form-label">Notes</label>
        <textarea
          className="form-control"
          rows={2}
          placeholder="What help is needed / development notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      {/* Previous cycles. Saving appends, never overwrites, so movement is visible. */}
      {history.length > 0 && (
        <div className="mt-4 assess-history">
          <h5>
            Assessment History<span className="count-chip">{history.length}</span>
          </h5>
          <p className="muted">
            The form above is prefilled with the latest. Saving records a new assessment
            rather than replacing this history.
          </p>
          <div className="table table-responsive">
            <table className="table-main table table-sm mt-2">
              <thead className="table-head">
                <tr className="table-head-row">
                  <th className="table-head-row">Date</th>
                  <th className="table-head-row">Type</th>
                  <th className="table-head-row">Readiness</th>
                  <th className="table-head-row">Performance</th>
                  <th className="table-head-row">Potential</th>
                  <th className="table-head-row">Risk of Loss</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {/* newest first — the most recent is what a reassessment is measured against */}
                {[...history].reverse().map((h) => (
                  <tr className="table-body-row" key={h._id}>
                    <td>{fmtDate(h.assessedAt)}</td>
                    <td>{h.assessmentType || "-"}</td>
                    <td>{h.readiness || "-"}</td>
                    <td>{h.performance || "-"}</td>
                    <td>{h.potential || "-"}</td>
                    <td>{h.riskOfLoss || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="modal-btn-wrapper mt-4 mb-4">
        <div>
          <button className="btn cancel-btn buttonsize" onClick={() => { setShowAssess(false); setAssessData(null); }}>
            Cancel
          </button>
        </div>
        <div>
          <button className="btn add-submit-button buttonsize" disabled={btnDisable} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessCandidate;
